import { GetStaticPropsResult, GetServerSidePropsContext, GetStaticPathsResult } from 'next';

import { getBlogPostsByRegion } from '../../../../src/api/contentful/blog/getBlogPostsByRegion';
import { getStateAgenciesByRegion } from '../../../../src/api/contentful/blog/getStateAgenciesByRegion';
import { getUSRegionByCode } from '../../../../src/api/contentful/blog/getUSRegionByCode';
import { getRutilusGraphqlClient } from '../../../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusStatic } from '../../../../src/api/graphql/queryRutilusStatic';
import { AppLogic } from '../../../../src/components/AppLogic/AppLogic';
import { buildSingleStateDataSpecies } from '../../../../src/components/FindUSSpecies/helpers/buildStateDataSpecies';
import { GlobalLayout } from '../../../../src/components/Layouts/GlobalLayout';
import { DISCOVER_PAGES_NAV_LINKS } from '../../../../src/constants/constants';
import { TOP_WATERS_BY_OUR_ANGLERS } from '../../../../src/constants/fishingWaters/topWatersByOurAnglers';
import { REVALIDATE } from '../../../../src/constants/next';
import TOP_SIX_SPECIES_BY_STATE from '../../../../src/constants/species/top_6_species_by_us_states_24042024.json';
import { US_STATES } from '../../../../src/constants/USstates/states';
import { splitIdAndSlug } from '../../../../src/helpers/urls';
import { GetRegion, GetRegionVariables } from '../../../../src/interfaces/graphql';
import { IPageMetadata } from '../../../../src/interfaces/nextjs';
import { getRegion } from '../../../../src/queries/rutilus/Regions';
import { Region, IRegionProps } from '../../../../src/views/Region/Region';

type IParams = {
  countryIdAndSlug: string;
  regionCodeAndSlug: string;
};

export async function getStaticProps({
  params,
}: GetServerSidePropsContext<IParams>): Promise<
  GetStaticPropsResult<IPageMetadata & IRegionProps>
> {
  const regionCodeAndSlug = params?.regionCodeAndSlug;
  const countryIdAndSlug = params?.countryIdAndSlug;

  if (!regionCodeAndSlug || !countryIdAndSlug) {
    return { notFound: true };
  }

  const containsUpperCase =
    regionCodeAndSlug !== regionCodeAndSlug.toLowerCase() ||
    countryIdAndSlug !== countryIdAndSlug.toLowerCase();

  if (containsUpperCase) {
    return {
      redirect: {
        destination: `/countries/${countryIdAndSlug}/regions/${regionCodeAndSlug}`.toLowerCase(),
        permanent: true,
      },
    };
  }
  const regionCode = splitIdAndSlug(regionCodeAndSlug);
  const countryId = splitIdAndSlug(countryIdAndSlug);
  const client = getRutilusGraphqlClient({ serverMode: 'static' });
  const { response } = await queryRutilusStatic<GetRegion, GetRegionVariables>(client, {
    query: getRegion,
    variables: {
      id: `${countryId}.${regionCode}`,
    },
  });
  const anglerReccomendedWatersList = TOP_WATERS_BY_OUR_ANGLERS.find(
    stateWaterObj => stateWaterObj.stateCode === regionCode.toUpperCase(),
  );

  if (!response) {
    return { notFound: true };
  }
  const stateDataSpecies = buildSingleStateDataSpecies(
    TOP_SIX_SPECIES_BY_STATE,
    regionCode.toUpperCase(),
  );

  const result = await getUSRegionByCode(regionCode.toUpperCase());
  const blogPostItems = result ? await getBlogPostsByRegion(3, result.sys.id) : undefined;
  const stateAgencyItems = result ? await getStateAgenciesByRegion(result.sys.id) : null;

  return {
    props: {
      _page: 'region',
      _pageTitle: 'Region',
      region: response.data.entry,
      regionCode,
      usStates: US_STATES,
      anglerReccomendedWatersList: anglerReccomendedWatersList ?? null,
      stateDataSpecies,
      blogPostItems: blogPostItems ? blogPostItems.items : [],
      stateAgencyItems: stateAgencyItems ? stateAgencyItems.items : null,
    },
    revalidate: REVALIDATE.DAILY,
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

Region.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout siteSubNavigationLinks={DISCOVER_PAGES_NAV_LINKS}>{page}</GlobalLayout>
    </AppLogic>
  );
};

export default Region;
